import React, {useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {EPasswordField} from '@ELib/components/form/EPasswordField';
import {ETextInput} from '@ELib/components/form/ETextInput';
import {useDebouncedValue} from '@ELib/utilities/hooks/debouncedValue';
import {PasswordValidityState} from '@ELib/utilities/types';

import {GroupWrapper} from '../sharedStyles/login';

import {passwordSatisfiesPolicy} from './resetLogic';
import {SubviewProps} from './types';

// no-op warning should not pop up
// eslint-disable-next-line no-console
const noop = () => console.warn('This field should not be editable');

export const PasswordStep = ({
  form,
  setForm,
}: SubviewProps): React.ReactElement => {
  const [debouncedLocalPassword, localPassword, setLocalPassword] =
    useDebouncedValue('', {timeout: 500});
  const [debouncedRepeatPassword, repeatedPassword, setRepeatedPassword] =
    useDebouncedValue('', {timeout: 500});
  const [samePassword, setSamePassword] = useState<boolean | null>(null);
  const [passwordValid, setPasswordValid] = useState<boolean | null>(null);
  const [checkingPassword, setCheckingPassword] = useState(false);

  const {t} = useTranslation('lib');

  const passwordValidityState = useMemo(() => {
    if (passwordValid === null) {
      return PasswordValidityState.NONE;
    }
    if (checkingPassword) {
      return PasswordValidityState.LOADING;
    }
    if (passwordValid) {
      return PasswordValidityState.VALID;
    }
    return PasswordValidityState.INVALID;
  }, [passwordValid, checkingPassword]);

  const repeatedValidityState = useMemo(() => {
    if (samePassword === null) {
      return PasswordValidityState.NONE;
    }
    if (samePassword) {
      return PasswordValidityState.VALID;
    }
    return PasswordValidityState.INVALID;
  }, [samePassword]);

  useEffect(() => {
    (async () => {
      setCheckingPassword(true);
      const policySatisfied =
        debouncedLocalPassword !== '' &&
        (await passwordSatisfiesPolicy(debouncedLocalPassword));
      setPasswordValid(policySatisfied);
      setCheckingPassword(false);
    })();
  }, [debouncedLocalPassword]);

  useEffect(() => {
    const isSamePassword =
      debouncedLocalPassword !== '' &&
      debouncedLocalPassword === debouncedRepeatPassword;
    setSamePassword(isSamePassword);
  }, [debouncedLocalPassword, debouncedRepeatPassword]);

  useEffect(() => {
    if (
      passwordValid &&
      samePassword &&
      debouncedLocalPassword !== form.password
    ) {
      setForm({...form, password: debouncedLocalPassword});
    } else if (form.password !== undefined) {
      setForm({...form, password: undefined});
    }
    // Only react to changes of passwordValid, samePassword and debouncedLocalPassword
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [samePassword, passwordValid, debouncedLocalPassword]);

  return (
    <>
      <GroupWrapper>
        <ETextInput
          label={t('login.inputs.username')}
          value={form.username}
          disabled
          onChange={noop}
        />
        <EPasswordField
          autoComplete='new-password' /* This is a necessary workaround as chrome ignores autoComplete="off" */
          bottomText={t('login.passwordReset.passwordRequirementsText')}
          label={t('login.inputs.newPassword')}
          validityState={passwordValidityState}
          value={localPassword}
          onChange={(event) => setLocalPassword(event.target.value)}
        />
        <EPasswordField
          autoComplete='new-password' /* This is a necessary workaround as chrome ignores autoComplete="off" */
          label={t('login.inputs.repeatPassword')}
          validityState={repeatedValidityState}
          value={repeatedPassword}
          onChange={(event) => setRepeatedPassword(event.target.value)}
        />
      </GroupWrapper>
    </>
  );
};
